import React, { useState, useEffect, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { CustomerRecord, Order } from '../types';
import { apiService } from '../services/apiService';
import { formatEuro, sanitizeWhatsAppPhone, formatDateTime } from '../utils/format';
import {
  Users,
  Search,
  MessageSquare,
  Phone,
  Calendar,
  ShoppingBag,
  TrendingUp,
  Award,
  Sparkles,
  ChevronDown,
  ChevronUp,
  FileText,
  Save,
  CheckCircle,
  Clock,
  ShieldCheck,
  RefreshCw,
  Plus,
  Trash2,
  Edit2,
  X,
  AlertCircle,
  UserPlus
} from 'lucide-react';

export const CustomerManagementStudio: React.FC = () => {
  const { allOrders, currentStaffRole } = useApp();

  const [customers, setCustomers] = useState<CustomerRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'repeat' | 'high_value'>('all');
  const [expandedCustomerId, setExpandedCustomerId] = useState<string | null>(null);

  // Notes editing state
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [feedbackSentId, setFeedbackSentId] = useState<string | null>(null);

  // Add Customer Modal State
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [addName, setAddName] = useState('');
  const [addPhone, setAddPhone] = useState('');
  const [addWhatsapp, setAddWhatsapp] = useState('');
  const [addNotes, setAddNotes] = useState('');
  const [addError, setAddError] = useState('');
  const [isSubmittingAdd, setIsSubmittingAdd] = useState(false);

  // Edit Customer Modal State
  const [editingCustomer, setEditingCustomer] = useState<CustomerRecord | null>(null);
  const [editName, setEditName] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editWhatsapp, setEditWhatsapp] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editVisitCount, setEditVisitCount] = useState<number>(1);
  const [editTotalSpent, setEditTotalSpent] = useState<number>(0);
  const [editError, setEditError] = useState('');
  const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);

  const fetchCustomerData = async () => {
    setIsLoading(true);
    try {
      const data = await apiService.getCustomers();
      if (Array.isArray(data) && data.length > 0) {
        setCustomers(data);
      } else {
        // Fallback aggregation from local allOrders
        const customerMap = new Map<string, CustomerRecord>();
        for (const order of allOrders) {
          if (!order.customer_phone && !order.customer_name) continue;
          const phone = order.customer_phone || `cust_${order.id}`;
          const existing = customerMap.get(phone);
          if (existing) {
            existing.visit_count += 1;
            existing.total_spent += Number(order.total_amount) || 0;
            if (!existing.orders) existing.orders = [];
            existing.orders.push(order);
          } else {
            customerMap.set(phone, {
              id: phone,
              name: order.customer_name || 'Asiakas',
              phone: order.customer_phone,
              whatsapp_number: order.customer_phone,
              visit_count: 1,
              total_spent: Number(order.total_amount) || 0,
              last_visit: order.created_at,
              created_at: order.created_at,
              orders: [order],
              notes: ''
            });
          }
        }
        setCustomers(Array.from(customerMap.values()));
      }
    } catch (e) {
      console.error('Error fetching customers:', e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomerData();
  }, [allOrders]);

  // Aggregated Stats
  const totalCustomers = customers.length;
  const totalLifetimeSpend = customers.reduce((sum, c) => sum + (Number(c.total_spent) || 0), 0);
  const avgSpendPerCustomer = totalCustomers > 0 ? totalLifetimeSpend / totalCustomers : 0;
  const repeatCustomersCount = customers.filter(c => (c.visit_count || 1) > 1).length;
  const repeatRate = totalCustomers > 0 ? Math.round((repeatCustomersCount / totalCustomers) * 100) : 0;

  // Filter & Search
  const filteredCustomers = useMemo(() => {
    return customers.filter(c => {
      const q = searchQuery.toLowerCase().trim();
      const nameMatch = (c.name || '').toLowerCase().includes(q);
      const phoneMatch = (c.phone || '').toLowerCase().includes(q);

      if (q && !nameMatch && !phoneMatch) return false;

      if (filterType === 'repeat') {
        return (c.visit_count || 1) > 1;
      }
      if (filterType === 'high_value') {
        return (c.total_spent || 0) >= 50;
      }
      return true;
    });
  }, [customers, searchQuery, filterType]);

  // Create Customer Handler
  const handleOpenAdd = () => {
    setAddName('');
    setAddPhone('');
    setAddWhatsapp('');
    setAddNotes('');
    setAddError('');
    setIsAddModalOpen(true);
  };

  const handleSaveAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addName.trim()) {
      setAddError('Customer Name is required.');
      return;
    }
    if (!addPhone.trim()) {
      setAddError('Customer Mobile Phone Number is required.');
      return;
    }

    setIsSubmittingAdd(true);
    setAddError('');

    const res = await apiService.createCustomer({
      name: addName.trim(),
      phone: addPhone.trim(),
      whatsapp_number: addWhatsapp.trim() || addPhone.trim(),
      notes: addNotes.trim()
    });

    setIsSubmittingAdd(false);

    if (res.success && res.data) {
      setCustomers(prev => [res.data!, ...prev.filter(c => c.id !== res.data!.id)]);
      setIsAddModalOpen(false);
    } else {
      setAddError(res.error || 'Failed to create customer');
    }
  };

  // Edit Customer Handler
  const handleOpenEdit = (c: CustomerRecord) => {
    setEditingCustomer(c);
    setEditName(c.name);
    setEditPhone(c.phone);
    setEditWhatsapp(c.whatsapp_number || c.phone);
    setEditNotes(c.notes || '');
    setEditVisitCount(c.visit_count || 1);
    setEditTotalSpent(c.total_spent || 0);
    setEditError('');
  };

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingCustomer) return;
    if (!editName.trim() || !editPhone.trim()) {
      setEditError('Name and Mobile Number cannot be empty.');
      return;
    }

    setIsSubmittingEdit(true);
    setEditError('');

    const updates = {
      name: editName.trim(),
      phone: editPhone.trim(),
      whatsapp_number: editWhatsapp.trim() || editPhone.trim(),
      notes: editNotes.trim(),
      visit_count: Number(editVisitCount) || 1,
      total_spent: Number(editTotalSpent) || 0
    };

    const res = await apiService.updateCustomer(editingCustomer.id, updates);
    setIsSubmittingEdit(false);

    if (res.success) {
      setCustomers(prev =>
        prev.map(c => (c.id === editingCustomer.id ? { ...c, ...updates } : c))
      );
      setEditingCustomer(null);
    } else {
      setEditError(res.error || 'Failed to update customer');
    }
  };

  // Delete Customer Handler
  const handleDeleteCustomer = async (c: CustomerRecord) => {
    if (!window.confirm(`Are you sure you want to delete customer "${c.name}" (${c.phone})? This cannot be undone.`)) {
      return;
    }

    const res = await apiService.deleteCustomer(c.id);
    if (res.success) {
      setCustomers(prev => prev.filter(item => item.id !== c.id));
    } else {
      alert(res.error || 'Failed to delete customer.');
    }
  };

  // WhatsApp Feedback Request Link Generator
  const handleSendWhatsAppFeedback = (customer: CustomerRecord) => {
    const phone = sanitizeWhatsAppPhone(customer.phone);
    if (!phone) {
      alert('Valid customer phone number not found.');
      return;
    }

    const latestOrder = customer.orders && customer.orders.length > 0 ? customer.orders[0] : null;
    const tableNum = latestOrder?.table_number ? ` (Pöytä #${latestOrder.table_number})` : '';

    const message = `Hei ${customer.name || 'asiakkaamme'}! 👋🍕\n\nKiitos paljon käynnistäsi The Oven Storyssa${tableNum}! Toivottavasti viihdyit ja artesaanipizzamme maistui.\n\nHaluaisimme kuulla mielipiteesi: miten ruokailukokemuksesi sujui? Voit vastata suoraan tähän viestiin tai jättää arvostelun:\nhttps://theovenstory.fi/feedback\n\nLämpimästi tervetuloa uudelleen!\n- The Oven Story Tiimi`;

    const encoded = encodeURIComponent(message);
    const url = `https://wa.me/${phone}?text=${encoded}`;

    window.open(url, '_blank');
    setFeedbackSentId(customer.id);
    setTimeout(() => setFeedbackSentId(null), 4000);
  };

  // Save Note shortcut
  const handleSaveNote = async (customerId: string) => {
    await apiService.updateCustomerNotes(customerId, noteDraft);
    setCustomers(prev =>
      prev.map(c => (c.id === customerId ? { ...c, notes: noteDraft } : c))
    );
    setEditingNoteId(null);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Header Row */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '14px',
          background: 'var(--bg-secondary)',
          padding: '20px 24px',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border-subtle)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div
            style={{
              width: '44px',
              height: '44px',
              borderRadius: 'var(--radius-md)',
              background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Users size={24} />
          </div>
          <div>
            <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.3rem', fontWeight: 800 }}>
              Customer Directory & CRM (Admin CRUD)
            </h2>
            <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
              Unique mobile phone registry, repeat customer tracking, lifetime spend, and order histories.
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button
            className="btn-secondary"
            onClick={fetchCustomerData}
            style={{ padding: '8px 16px', fontSize: '0.85rem', gap: '6px' }}
          >
            <RefreshCw size={15} className={isLoading ? 'anim-spin' : ''} />
            <span>Refresh</span>
          </button>
          <button
            className="btn-primary"
            onClick={handleOpenAdd}
            style={{ padding: '8px 18px', fontSize: '0.88rem', gap: '6px' }}
          >
            <UserPlus size={16} />
            <span>Add Customer</span>
          </button>
        </div>
      </div>

      {/* 4 Overview Metric Cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '14px'
        }}
      >
        <div
          style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-lg)',
            padding: '16px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: '14px'
          }}
        >
          <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'rgba(59, 130, 246, 0.12)', color: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Users size={20} />
          </div>
          <div>
            <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>
              Total Guests
            </div>
            <div style={{ fontSize: '1.4rem', fontWeight: 900, color: 'var(--text-primary)' }}>
              {totalCustomers}
            </div>
          </div>
        </div>

        <div
          style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-lg)',
            padding: '16px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: '14px'
          }}
        >
          <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'rgba(16, 185, 129, 0.12)', color: 'var(--accent-emerald)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <TrendingUp size={20} />
          </div>
          <div>
            <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>
              Total Spend (EUR)
            </div>
            <div style={{ fontSize: '1.4rem', fontWeight: 900, color: 'var(--accent-copper)' }}>
              {formatEuro(totalLifetimeSpend)}
            </div>
          </div>
        </div>

        <div
          style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-lg)',
            padding: '16px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: '14px'
          }}
        >
          <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'rgba(224, 90, 54, 0.12)', color: 'var(--accent-copper)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Award size={20} />
          </div>
          <div>
            <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>
              Repeat Customers
            </div>
            <div style={{ fontSize: '1.4rem', fontWeight: 900, color: 'var(--text-primary)' }}>
              {repeatCustomersCount}{' '}
              <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontWeight: 600 }}>({repeatRate}%)</span>
            </div>
          </div>
        </div>

        <div
          style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-lg)',
            padding: '16px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: '14px'
          }}
        >
          <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'rgba(245, 158, 11, 0.12)', color: 'var(--accent-amber)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Sparkles size={20} />
          </div>
          <div>
            <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>
              Avg Spend / Guest
            </div>
            <div style={{ fontSize: '1.4rem', fontWeight: 900, color: 'var(--text-primary)' }}>
              {formatEuro(avgSpendPerCustomer)}
            </div>
          </div>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div
        style={{
          background: 'var(--bg-secondary)',
          padding: '16px 20px',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border-subtle)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '14px'
        }}
      >
        <div style={{ position: 'relative', minWidth: '280px', flex: 1 }}>
          <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input
            type="text"
            className="form-input"
            placeholder="Search by customer name or phone number..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{ paddingLeft: '38px', height: '40px' }}
          />
        </div>

        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            className={`btn-secondary ${filterType === 'all' ? 'active-tab' : ''}`}
            onClick={() => setFilterType('all')}
            style={{ padding: '6px 14px', fontSize: '0.82rem' }}
          >
            All Guests ({totalCustomers})
          </button>
          <button
            className={`btn-secondary ${filterType === 'repeat' ? 'active-tab' : ''}`}
            onClick={() => setFilterType('repeat')}
            style={{ padding: '6px 14px', fontSize: '0.82rem' }}
          >
            🔁 Repeat Guests ({repeatCustomersCount})
          </button>
          <button
            className={`btn-secondary ${filterType === 'high_value' ? 'active-tab' : ''}`}
            onClick={() => setFilterType('high_value')}
            style={{ padding: '6px 14px', fontSize: '0.82rem' }}
          >
            ⭐ VIP (Spend &gt; €50)
          </button>
        </div>
      </div>

      {/* Customer Records Table */}
      <div
        style={{
          background: 'var(--bg-card)',
          borderRadius: 'var(--radius-xl)',
          border: '1px solid var(--border-subtle)',
          overflow: 'hidden'
        }}
      >
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.88rem' }}>
            <thead>
              <tr style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border-subtle)', color: 'var(--text-muted)', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                <th style={{ padding: '14px 20px' }}>Customer Name & Phone</th>
                <th style={{ padding: '14px 16px' }}>Status & Loyalty</th>
                <th style={{ padding: '14px 16px' }}>Visits</th>
                <th style={{ padding: '14px 16px' }}>Total Spend</th>
                <th style={{ padding: '14px 16px' }}>Last Visit</th>
                <th style={{ padding: '14px 20px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCustomers.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--text-muted)' }}>
                    No customer records matching your filter.
                  </td>
                </tr>
              ) : (
                filteredCustomers.map(customer => {
                  const isRepeat = (customer.visit_count || 1) > 1;
                  const isExpanded = expandedCustomerId === customer.id;

                  return (
                    <React.Fragment key={customer.id}>
                      <tr
                        style={{
                          borderBottom: '1px solid var(--border-subtle)',
                          transition: 'background 0.15s ease',
                          background: isExpanded ? 'var(--bg-secondary)' : 'transparent'
                        }}
                      >
                        {/* Name & Phone */}
                        <td style={{ padding: '16px 20px' }}>
                          <div style={{ fontWeight: 800, fontSize: '0.96rem', color: 'var(--text-primary)' }}>
                            {customer.name}
                          </div>
                          <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px' }}>
                            <Phone size={12} />
                            <span>{customer.phone || customer.id}</span>
                          </div>
                        </td>

                        {/* Status / Loyalty Badge */}
                        <td style={{ padding: '16px 16px' }}>
                          {isRepeat ? (
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                background: 'rgba(224, 90, 54, 0.12)',
                                color: 'var(--accent-copper)',
                                padding: '3px 10px',
                                borderRadius: 'var(--radius-full)',
                                fontSize: '0.76rem',
                                fontWeight: 700
                              }}
                            >
                              🔁 Repeat Guest ({customer.visit_count} visits)
                            </span>
                          ) : (
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                background: 'var(--bg-secondary)',
                                color: 'var(--text-muted)',
                                padding: '3px 10px',
                                borderRadius: 'var(--radius-full)',
                                fontSize: '0.76rem',
                                fontWeight: 600
                              }}
                            >
                              New Guest (1st visit)
                            </span>
                          )}
                        </td>

                        {/* Visit Count */}
                        <td style={{ padding: '16px 16px', fontWeight: 700 }}>
                          {customer.visit_count || 1}
                        </td>

                        {/* Total Spend */}
                        <td style={{ padding: '16px 16px', fontWeight: 800, color: 'var(--accent-copper)' }}>
                          {formatEuro(customer.total_spent || 0)}
                        </td>

                        {/* Last Visit */}
                        <td style={{ padding: '16px 16px', color: 'var(--text-muted)', fontSize: '0.82rem' }}>
                          {customer.last_visit ? formatDateTime(customer.last_visit) : 'Recently'}
                        </td>

                        {/* Action Buttons */}
                        <td style={{ padding: '16px 20px', textAlign: 'right' }}>
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                            <button
                              className="btn-secondary"
                              onClick={() => handleSendWhatsAppFeedback(customer)}
                              title="Send WhatsApp Satisfaction Message"
                              style={{ padding: '6px 10px', fontSize: '0.78rem', gap: '4px', color: 'var(--accent-emerald)' }}
                            >
                              <MessageSquare size={13} />
                              <span className="desktop-only">WhatsApp</span>
                            </button>

                            <button
                              className="btn-secondary"
                              onClick={() => handleOpenEdit(customer)}
                              title="Edit Customer"
                              style={{ padding: '6px 10px', fontSize: '0.78rem' }}
                            >
                              <Edit2 size={13} />
                            </button>

                            <button
                              className="btn-secondary"
                              onClick={() => handleDeleteCustomer(customer)}
                              title="Delete Customer"
                              style={{ padding: '6px 10px', fontSize: '0.78rem', color: 'var(--accent-crimson)' }}
                            >
                              <Trash2 size={13} />
                            </button>

                            <button
                              className="btn-secondary"
                              onClick={() => setExpandedCustomerId(isExpanded ? null : customer.id)}
                              title="Toggle Order History & Notes"
                              style={{ padding: '6px 10px', fontSize: '0.78rem' }}
                            >
                              {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                            </button>
                          </div>
                        </td>
                      </tr>

                      {/* Expanded Row for Order History & Notes */}
                      {isExpanded && (
                        <tr>
                          <td colSpan={6} style={{ background: 'var(--bg-secondary)', padding: '20px 24px', borderBottom: '1px solid var(--border-subtle)' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                              {/* Left: Notes */}
                              <div style={{ background: 'var(--bg-card)', padding: '16px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                  <span style={{ fontWeight: 800, fontSize: '0.85rem' }}>Staff Notes & Preferences</span>
                                  {editingNoteId !== customer.id ? (
                                    <button
                                      className="btn-secondary"
                                      onClick={() => {
                                        setEditingNoteId(customer.id);
                                        setNoteDraft(customer.notes || '');
                                      }}
                                      style={{ padding: '3px 8px', fontSize: '0.75rem' }}
                                    >
                                      Edit Notes
                                    </button>
                                  ) : (
                                    <button
                                      className="btn-primary"
                                      onClick={() => handleSaveNote(customer.id)}
                                      style={{ padding: '3px 8px', fontSize: '0.75rem' }}
                                    >
                                      Save Notes
                                    </button>
                                  )}
                                </div>

                                {editingNoteId === customer.id ? (
                                  <textarea
                                    className="form-input"
                                    rows={3}
                                    value={noteDraft}
                                    onChange={e => setNoteDraft(e.target.value)}
                                    placeholder="Customer dining preferences, allergies, favorite items..."
                                    style={{ fontSize: '0.85rem' }}
                                  />
                                ) : (
                                  <div style={{ fontSize: '0.85rem', color: customer.notes ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                                    {customer.notes || 'No staff notes recorded yet.'}
                                  </div>
                                )}
                              </div>

                              {/* Right: Past Orders */}
                              <div style={{ background: 'var(--bg-card)', padding: '16px', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)' }}>
                                <div style={{ fontWeight: 800, fontSize: '0.85rem', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <ShoppingBag size={15} color="var(--accent-copper)" />
                                  <span>Lifetime Order History ({customer.orders?.length || 0} orders)</span>
                                </div>

                                {!customer.orders || customer.orders.length === 0 ? (
                                  <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                                    No recorded orders in current log.
                                  </div>
                                ) : (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '180px', overflowY: 'auto' }}>
                                    {customer.orders.map((ord: Order) => (
                                      <div
                                        key={ord.id}
                                        style={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                          padding: '6px 10px',
                                          background: 'var(--bg-secondary)',
                                          borderRadius: 'var(--radius-sm)',
                                          fontSize: '0.8rem'
                                        }}
                                      >
                                        <div>
                                          <strong>{ord.order_number}</strong> (Pöytä #{ord.table_number}) • {ord.items?.length || 0} items
                                        </div>
                                        <div style={{ fontWeight: 700, color: 'var(--accent-copper)' }}>
                                          {formatEuro(ord.total_amount)}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 1. ADD CUSTOMER MODAL */}
      {isAddModalOpen && (
        <div className="modal-backdrop" onClick={() => setIsAddModalOpen(false)}>
          <div className="modal-card" onClick={e => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontWeight: 800, fontSize: '1.2rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <UserPlus size={20} color="var(--accent-copper)" />
                <span>Add New Customer</span>
              </h3>
              <button onClick={() => setIsAddModalOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                <X size={20} />
              </button>
            </div>

            {addError && (
              <div style={{ padding: '10px 14px', background: 'rgba(239, 68, 68, 0.12)', color: 'var(--accent-crimson)', borderRadius: 'var(--radius-md)', marginBottom: '14px', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <AlertCircle size={16} />
                <span>{addError}</span>
              </div>
            )}

            <form onSubmit={handleSaveAdd} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div className="form-group">
                <label className="form-label">Customer Full Name *</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g. Matti Virtanen"
                  value={addName}
                  onChange={e => setAddName(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Mobile Phone Number (Unique Identifier) *</label>
                <input
                  type="tel"
                  className="form-input"
                  placeholder="+358 40 123 4567"
                  value={addPhone}
                  onChange={e => setAddPhone(e.target.value)}
                  required
                />
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '4px' }}>
                  Customer order histories and repeat visit metrics will be linked to this unique number.
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">WhatsApp Number (Optional, defaults to mobile)</label>
                <input
                  type="tel"
                  className="form-input"
                  placeholder="+358 40 123 4567"
                  value={addWhatsapp}
                  onChange={e => setAddWhatsapp(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Dietary Preferences / Staff Notes</label>
                <textarea
                  className="form-input"
                  rows={3}
                  placeholder="Gluten-free, favorite pizza, VIP notes..."
                  value={addNotes}
                  onChange={e => setAddNotes(e.target.value)}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
                <button type="button" className="btn-secondary" onClick={() => setIsAddModalOpen(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={isSubmittingAdd}>
                  {isSubmittingAdd ? 'Saving...' : 'Create Customer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 2. EDIT CUSTOMER MODAL */}
      {editingCustomer && (
        <div className="modal-backdrop" onClick={() => setEditingCustomer(null)}>
          <div className="modal-card" onClick={e => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontWeight: 800, fontSize: '1.2rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Edit2 size={20} color="var(--accent-copper)" />
                <span>Edit Customer</span>
              </h3>
              <button onClick={() => setEditingCustomer(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
                <X size={20} />
              </button>
            </div>

            {editError && (
              <div style={{ padding: '10px 14px', background: 'rgba(239, 68, 68, 0.12)', color: 'var(--accent-crimson)', borderRadius: 'var(--radius-md)', marginBottom: '14px', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <AlertCircle size={16} />
                <span>{editError}</span>
              </div>
            )}

            <form onSubmit={handleSaveEdit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div className="form-group">
                <label className="form-label">Customer Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={editName}
                  onChange={e => setEditName(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Mobile Phone Number</label>
                <input
                  type="tel"
                  className="form-input"
                  value={editPhone}
                  onChange={e => setEditPhone(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">WhatsApp Number</label>
                <input
                  type="tel"
                  className="form-input"
                  value={editWhatsapp}
                  onChange={e => setEditWhatsapp(e.target.value)}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="form-group">
                  <label className="form-label">Visit Count</label>
                  <input
                    type="number"
                    min="1"
                    className="form-input"
                    value={editVisitCount}
                    onChange={e => setEditVisitCount(Number(e.target.value))}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Total Spend (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="form-input"
                    value={editTotalSpent}
                    onChange={e => setEditTotalSpent(Number(e.target.value))}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Staff Notes & Preferences</label>
                <textarea
                  className="form-input"
                  rows={3}
                  value={editNotes}
                  onChange={e => setEditNotes(e.target.value)}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
                <button type="button" className="btn-secondary" onClick={() => setEditingCustomer(null)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={isSubmittingEdit}>
                  {isSubmittingEdit ? 'Saving...' : 'Update Customer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
